import { logApiError, logApiPerformance } from '@ms/yammer-diagnostics-state';
import { getErrorMessage, getErrorReason, getPerformanceMeasureEntry, reportError } from '@ms/yammer-telemetry';
import { acquireToken } from '@ms/yammer-web-authenticators';

import { isFeatureRolloutEnabled } from '../domains/featureRollouts/selectors';
import type { PromiseThunkAction } from '../types';

import apiRequest from './apiRequest';
import { parseChallenges } from './caeParseChallenges';
import type { GraphqlApiError } from './errors';
import { ApiError, isApiError, isGraphqlApiError } from './errors';
import { getResourcesOrScopes } from './getResourcesOrScopes';
import {
  graphqlApiRequestWithAutomaticPersistedQueries,
  graphqlApiRequestWithoutAutomaticPersistedQueries,
} from './graphqlApiRequest';
import { setImageRenderingCookie } from './imageRenderingCookie';
import { applyMiddleware } from './middleware';
import { endFetchMeasurement, getApiMeasureName, startFetchMeasurement } from './performance';
import sharePointApiRequest from './sharePointApiRequest';
import type { ApiRequest, ApiRequestType, ExecuteRequest, ManagedApiRequest, ManagedApiTelemetry } from './types';

const apiAction =
  <TResponse>(request: ManagedApiRequest, telemetry?: ManagedApiTelemetry): PromiseThunkAction<TResponse> =>
  async (dispatch) => {
    try {
      if (request.skipTokenCheck !== true) {
        await dispatch(ensureTokens(request));
      }

      const apiRequestPromise = dispatch(makeApiRequest<TResponse>(request, telemetry));
      if (request.skipTokenCheck !== true) {
        // Set the image rendering cookie in parallel but don't wait for its result
        dispatch(setImageRenderingCookie());
      }
      const response = await apiRequestPromise;

      return response;
    } catch (error) {
      if (!isRetriableError(error)) {
        throw error;
      }

      reportError({ error });

      if (error instanceof ApiError && error.wwwAuthenticate) {
        const claimsChallenge = parseChallenges(error.wwwAuthenticate)?.claims;
        // eslint-disable-next-line no-restricted-globals
        const claims = claimsChallenge ? atob(claimsChallenge) : undefined;

        return dispatch(refreshTokensAndRetry<TResponse>(request, claims));
      }

      return dispatch(refreshTokensAndRetry<TResponse>(request));
    }
  };

const ensureTokens =
  (request: ManagedApiRequest, claims?: string): PromiseThunkAction<void> =>
  async (_dispatch, getState) => {
    const state = getState();
    const resourcesOrScopes = getResourcesOrScopes(request, state);

    await acquireToken({ scopes: resourcesOrScopes, claims });
  };

const isRetriableError = (error: unknown) => {
  const status = (error as ApiError).status;
  const isUnauthorizedError = status === 401;
  const isUnknownError = status == null;

  return isUnauthorizedError || isUnknownError;
};

const refreshTokensAndRetry =
  <TResponse>(request: ManagedApiRequest, claims?: string): PromiseThunkAction<TResponse> =>
  async (dispatch) => {
    if (request.skipTokenCheck !== true) {
      await dispatch(ensureTokens(request, claims));
    }

    return dispatch(makeApiRequest<TResponse>(request));
  };

const getRequestExecutor = (requestType: ApiRequestType, isAutomaticPersistedQueriesEnabled: boolean) => {
  switch (requestType) {
    case 'Graphql':
      return isAutomaticPersistedQueriesEnabled
        ? graphqlApiRequestWithAutomaticPersistedQueries
        : graphqlApiRequestWithoutAutomaticPersistedQueries;
    case 'SharePoint':
      return sharePointApiRequest;
    default:
      return apiRequest;
  }
};

const makeApiRequest =
  <TResponse>(request: ManagedApiRequest, telemetry?: ManagedApiTelemetry): PromiseThunkAction<TResponse> =>
  async (_dispatch, getState) => {
    const state = getState();
    const isAutomaticPersistedQueriesEnabled = isFeatureRolloutEnabled(state, 'AutomaticPersistedQueries');
    const { requestType } = request;
    const finalRequest = await applyMiddleware(request, state);
    const executeRequest = getRequestExecutor(requestType, isAutomaticPersistedQueriesEnabled);

    return makePerformanceReportedApiRequest<TResponse>(finalRequest, executeRequest, telemetry);
  };

const extractAfdHeader = (headers: Headers) => headers.get('X-MSEdge-Ref') || undefined;
const extractTraceIdHeader = (headers: Headers) => headers.get('x-trace-id') || undefined;

const makePerformanceReportedApiRequest = async <TResponse>(
  request: ApiRequest,
  executeRequest: ExecuteRequest<TResponse>,
  telemetry?: ManagedApiTelemetry
): Promise<TResponse> => {
  const apiMeasureName = getApiMeasureName(request);
  let requestRejected = false;
  let error: Error | GraphqlApiError | ApiError | undefined;
  let afdRef: string | undefined;
  let traceId;
  const measure = startFetchMeasurement(apiMeasureName);
  try {
    const { body, headers } = await executeRequest(request);
    afdRef = extractAfdHeader(headers);
    traceId = extractTraceIdHeader(headers);

    return body;
  } catch (err) {
    requestRejected = true;
    error = err as ApiError | GraphqlApiError;
    let requestId;
    if (isGraphqlApiError(error) || isApiError(error)) {
      afdRef = error.afdRef || undefined;
      traceId = error.traceId || undefined;
      requestId = error.requestId || undefined;
    }
    logApiError({
      name: apiMeasureName,
      traceId,
      requestId,
      message: error.message,
    });
    throw err;
  } finally {
    const requestId = request.headers?.['X-Request-Id'] || '';
    endFetchMeasurement({
      ...measure,
      eventProperties: {
        ...measure.eventProperties,
        apiName: apiMeasureName,
        actionName: telemetry?.actionName,
        actionType: telemetry?.actionType,
        endedWithError: requestRejected,
        message: getErrorMessage(error),
        reason: getErrorReason(error),
        requestId,
        initiator: request.initiator,
        afdRef,
        ...telemetry?.customProperties,
      },
    });
    const { duration } = getPerformanceMeasureEntry(measure.measureKey);
    logApiPerformance({
      name: apiMeasureName,
      traceId,
      duration,
      requestId,
    });
  }
};

export default apiAction;
